import { router, usePage } from '@inertiajs/react';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import TableChartIcon from '@mui/icons-material/TableChart';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useForkRef } from '@mui/material/utils';
import {
  useParsedFormat,
  usePickerContext,
  useSplitFieldProps,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import MenuButton from '../../components/MenuButton';
import NavbarBreadcrumbs from '../../components/NavbarBreadcrumbs';
import ColorModeIconDropdown from '../../theme/ColorModeIconDropdown';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.locale('pt-br');

function ButtonField(props) {
  const { forwardedProps } = useSplitFieldProps(props, 'date');
  const pickerContext = usePickerContext();
  const handleRef = useForkRef(pickerContext.triggerRef, pickerContext.rootRef);
  const parsedFormat = useParsedFormat();
  const valueStr =
    pickerContext.value == null
      ? parsedFormat
      : pickerContext.value.format(pickerContext.fieldFormat);

  return (
    <Button
      {...forwardedProps}
      variant="outlined"
      ref={handleRef}
      size="small"
      startIcon={<CalendarTodayRoundedIcon fontSize="small" />}
      sx={{ minWidth: 'fit-content' }}
      onClick={() => pickerContext.setOpen((prev) => !prev)}
    >
      {pickerContext.label ?? valueStr}
    </Button>
  );
}

export default function AnalyticsHeader() {
  const { props } = usePage();
  const currentDate = props.q?.day_eq ? dayjs(props.q.day_eq) : dayjs();
  const [value, setValue] = React.useState(currentDate);
  const [loading, setLoading] = React.useState(false);

  const handleDateChange = (newValue) => {
    setLoading(true);
    setValue(newValue);

    const params = newValue ? { q: { day_eq: newValue.format('YYYY-MM-DD') } } : {};
    router.get('/analytics', params, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
    setLoading(false);
  };

  const handleDataStreamsClick = () => {
    router.get('/data_streams');
  };

  const getLabel = (value) => {
    if (value == null) return null;
    if (value.isSame(dayjs(), 'day')) return 'Hoje';
    if (value.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Ontem';

    return value.format('DD/MM/YYYY');
  };

  return (
    <Stack
      direction="row"
      sx={{
        display: { xs: 'none', md: 'flex' },
        width: '100%',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        maxWidth: { sm: '100%', md: '1700px' },
        pt: 1.5,
      }}
      spacing={2}
    >
      <NavbarBreadcrumbs />
      <Stack direction="row" sx={{ gap: 1 }}>
        {/* Botão Ver Planilhas */}
        <Button
          variant="outlined"
          startIcon={<TableChartIcon />}
          onClick={handleDataStreamsClick}
          sx={{
            minWidth: 'fit-content',
            color: 'text.secondary',
            borderColor: 'divider',
            height: '36px'
          }}
        >
          Ver Planilhas
        </Button>

        {/* Filtro de Data - Seguindo padrão TowerDatePicker */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            utc={true}
            timezone='America/Sao_Paulo'
            loading={loading}
            disableFuture
            adapterLocale="pt-br"
            value={value}
            label={getLabel(value)}
            onChange={handleDateChange}
            slots={{ field: ButtonField }}
            slotProps={{
              nextIconButton: { size: 'small' },
              previousIconButton: { size: 'small' },
              calendarHeader: {
                sx: {
                  '.MuiPickersCalendarHeader-label': {
                    textTransform: 'capitalize',
                  },
                },
              },
            }}
            views={['day']}
          />
        </LocalizationProvider>

        <MenuButton showBadge aria-label="Notificações">
          <NotificationsRoundedIcon />
        </MenuButton>
        <ColorModeIconDropdown />
      </Stack>
    </Stack>
  );
} 